(() => {
  'use strict';
  const cache = new Map();
  const forms = new Set();
  let serial = 0;
  function safeUrl(value, localOnly = false) {
    try {
      const url = new URL(value, location.origin);
      if (!['http:', 'https:'].includes(url.protocol) || (localOnly && url.origin !== location.origin)) return '';
      return url.href;
    } catch { return ''; }
  }
  function enhance(form) {
    if (form.dataset.searchReady) return;
    const input = form.querySelector('[data-search-input]');
    const menu = form.querySelector('[data-search-suggestions]');
    const clear = form.querySelector('[data-search-clear]');
    if (!input || !menu || !clear) return;
    form.dataset.searchReady = 'true';
    let copy;
    try { copy = JSON.parse(form.querySelector('[data-search-copy]').textContent); } catch { return; }
    const id = 'motai-search-options-' + (++serial);
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', id);
    let timer;
    let controller;
    let revision = 0;
    let composing = false;
    let links = [];
    let active = -1;
    let renderedQuery = '';
    const state = { close, place, form };
    forms.add(state);
    function cancel() {
      clearTimeout(timer);
      controller?.abort();
      revision++;
      input.removeAttribute('aria-busy');
    }
    function close() {
      cancel();
      menu.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
    }
    function place() {
      if (menu.hidden) return;
      const rect = menu.parentElement.getBoundingClientRect();
      const view = window.visualViewport;
      const top = (view?.offsetTop || 0) + 8;
      let bottom = top + (view?.height || innerHeight) - 16;
      const contact = document.querySelector('.mobile-contact')?.getBoundingClientRect();
      if (contact?.height && contact.top < bottom) bottom = contact.top - 8;
      const header = document.querySelector('header')?.getBoundingClientRect();
      const aboveTop = header?.top >= 0 && !form.closest('header') ? Math.max(top, header.bottom + 8) : top;
      const above = Math.max(0, rect.top - aboveTop - 8);
      const below = Math.max(0, bottom - rect.bottom - 8);
      const desired = Math.min(420, menu.scrollHeight);
      const up = below < desired && above > below;
      const available = up ? above : below;
      if (rect.bottom < top || rect.top > bottom || available < 60) { close(); return; }
      menu.classList.toggle('is-above', up);
      menu.style.maxHeight = Math.min(420, available) + 'px';
    }
    function open() {
      for (const other of forms) if (other !== state) other.close();
      menu.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      place();
    }
    function searchUrl(query, suggest = false) {
      const url = new URL(form.action, location.origin);
      url.search = '';
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('sort_by', 'relevance');
      if (suggest) url.searchParams.set('view', 'suggestions');
      return url;
    }
    function select(index) {
      active = index;
      links.forEach((link, i) => link.setAttribute('aria-selected', String(i === active)));
      if (links[active]) {
        input.setAttribute('aria-activedescendant', links[active].id);
        links[active].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else input.removeAttribute('aria-activedescendant');
    }
    function status(text) {
      const node = document.createElement('p');
      node.className = 'motai-search-status';
      node.setAttribute('role', 'status');
      node.textContent = text;
      menu.append(node);
    }
    function paint(products, query, failed = false) {
      menu.replaceChildren();
      links = [];
      active = -1;
      renderedQuery = query;
      input.removeAttribute('aria-activedescendant');
      if (products.length) status(copy.suggestions_label);
      else if (!failed) status(copy.no_suggestions);
      const list = document.createElement('div');
      list.id = id;
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', copy.suggestions_label);
      products.forEach(product => {
        const href = safeUrl(product.url, true);
        if (!href) return;
        const link = document.createElement('a');
        link.href = href;
        link.className = 'motai-search-suggestion';
        const imageUrl = safeUrl(product.image || '');
        if (product.image && imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = '';
          image.width = 48;
          image.height = 48;
          image.loading = 'lazy';
          link.append(image);
        }
        const text = document.createElement('span');
        text.className = 'motai-search-suggestion-copy';
        const title = document.createElement('strong');
        title.textContent = product.title;
        text.append(title);
        if (product.type) {
          const type = document.createElement('small');
          type.textContent = product.type;
          text.append(type);
        }
        link.append(text);
        list.append(link);
        links.push(link);
      });
      const all = document.createElement('a');
      all.href = searchUrl(query).href;
      all.className = 'motai-search-all';
      all.textContent = copy.search_all;
      list.append(all);
      links.push(all);
      links.forEach((link, index) => {
        link.id = id + '-' + index;
        link.tabIndex = -1;
        link.setAttribute('role', 'option');
        link.setAttribute('aria-selected', 'false');
      });
      menu.append(list);
      open();
    }
    async function request() {
      const query = input.value.trim();
      if (!query || composing) { close(); return; }
      cancel();
      const current = revision;
      const url = searchUrl(query, true).href;
      if (cache.has(url)) { paint(cache.get(url), query); return; }
      const requestController = new AbortController();
      controller = requestController;
      const timeout = setTimeout(() => requestController.abort(), 5000);
      input.setAttribute('aria-busy', 'true');
      menu.replaceChildren();
      const loadingList = document.createElement('div');
      loadingList.id = id;
      loadingList.setAttribute('role', 'listbox');
      menu.append(loadingList);
      status(copy.loading);
      links = [];
      active = -1;
      input.removeAttribute('aria-activedescendant');
      open();
      try {
        const response = await fetch(url, { signal: requestController.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!doc.querySelector('[data-search-resultset]')) throw new Error('Search unavailable');
        const products = [...doc.querySelectorAll('[data-search-product]')].slice(0, 6).map(el => ({
          title: el.dataset.title || '', type: el.dataset.type || '', url: el.getAttribute('href'), image: el.querySelector('img')?.getAttribute('src')
        }));
        cache.set(url, products);
        if (cache.size > 20) cache.delete(cache.keys().next().value);
        if (current === revision && input.value.trim() === query && form.contains(document.activeElement)) paint(products, query);
      } catch {
        if (current === revision && input.value.trim() === query && form.contains(document.activeElement)) paint([], query, true);
      } finally {
        clearTimeout(timeout);
        if (current === revision) input.removeAttribute('aria-busy');
      }
    }
    function queue() {
      clear.hidden = !input.value;
      cancel();
      if (!input.value.trim() || composing) { close(); return; }
      menu.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      timer = setTimeout(request, 280);
    }
    input.addEventListener('input', queue);
    input.addEventListener('search', queue);
    input.addEventListener('compositionstart', () => { composing = true; close(); });
    input.addEventListener('compositionend', () => { composing = false; queue(); });
    input.addEventListener('focus', () => { if (input.value.trim()) queue(); });
    input.addEventListener('keydown', async event => {
      if (event.isComposing || composing) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        if (form.classList.contains('motai-nav-search')) {
          form.hidden = true;
          const toggle = document.querySelector('.motai-search-toggle');
          toggle?.setAttribute('aria-expanded', 'false');
          toggle?.focus();
        }
      } else if (['ArrowDown', 'ArrowUp'].includes(event.key) && input.value.trim()) {
        event.preventDefault();
        if (menu.hidden || renderedQuery !== input.value.trim()) await request();
        if (links.length && !menu.hidden) {
          const first = event.key === 'ArrowDown' ? 0 : links.length - 1;
          select(active < 0 ? first : (active + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length);
        }
      } else if (event.key === 'Enter' && !menu.hidden && links[active]) {
        event.preventDefault();
        links[active].click();
      } else if (event.key === 'Tab') close();
    });
    clear.addEventListener('click', () => {
      input.value = '';
      clear.hidden = true;
      close();
      input.focus();
    });
    form.addEventListener('submit', event => {
      input.value = input.value.trim();
      if (!input.value) { event.preventDefault(); input.reportValidity(); }
      close();
    });
    form.addEventListener('keydown', event => {
      if (event.key === 'Escape' && event.target !== input && form.classList.contains('motai-nav-search')) {
        event.preventDefault();
        event.stopPropagation();
        close();
        form.hidden = true;
        const toggle = document.querySelector('.motai-search-toggle');
        toggle?.setAttribute('aria-expanded', 'false');
        toggle?.focus();
      }
    });
    form.addEventListener('focusout', event => { if (!form.contains(event.relatedTarget)) close(); });
    document.addEventListener('click', event => {
      if (!form.contains(event.target)) close();
      if (form.classList.contains('motai-nav-search') && !form.hidden && !form.closest('header')?.contains(event.target)) {
        form.hidden = true;
        document.querySelector('.motai-search-toggle')?.setAttribute('aria-expanded', 'false');
      }
    });
    clear.hidden = !input.value;
  }
  document.querySelectorAll('[data-search-form]').forEach(enhance);
  window.addEventListener('resize', () => { for (const state of forms) state.place(); });
  window.addEventListener('scroll', () => { for (const state of forms) state.place(); }, { passive: true, capture: true });
  window.visualViewport?.addEventListener('resize', () => { for (const state of forms) state.place(); });
  document.addEventListener('shopify:section:load', event => { event.target.querySelectorAll('[data-search-form]').forEach(enhance); });
})();
